import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Wallet dashboard · the UBI rolling thirty-day cash-out window for a
 * logged-in user. Pulls from the wallet endpoints landed in bake #9.
 *
 * The rules, plain:
 *   - Every hour of work credits your wallet immediately as in-store credit.
 *   - Each individual credit unlocks for cash 30 days after it was earned.
 *   - Monthly automatic bank payout is the default. Weekly and daily are
 *     promises that turn on when revenue supports them.
 */
export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [status, setStatus] = useState({ loading: true, error: null, unauthenticated: false });

  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setStatus({ loading: false, error: null, unauthenticated: true });
      return;
    }
    fetch(`${API}/api/sage/ubi/wallet/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (r.status === 401) {
          setStatus({ loading: false, error: null, unauthenticated: true });
          return null;
        }
        if (!r.ok) throw new Error(`server said ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (data) {
          setWallet(data);
          setStatus({ loading: false, error: null, unauthenticated: false });
        }
      })
      .catch((e) => setStatus({ loading: false, error: String(e.message || e), unauthenticated: false }));
  }, []);

  if (status.unauthenticated) {
    return (
      <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e] flex items-center justify-center px-6" data-testid="wallet-signin">
        <div className="max-w-lg text-center">
          <h1 className="text-4xl font-serif text-[#2c3e2a] border-b-2 border-[#b58c2a] pb-4">
            Sign in to see your wallet
          </h1>
          <p className="mt-6 text-lg font-serif text-[#4a5a48]">
            Your rolling thirty-day cash-out sits behind a sign-in door. One click with your Google account.
          </p>
          <a
            href="/founder-login"
            className="inline-block mt-8 bg-[#2c3e2a] text-[#fbfaf6] py-3 px-8 rounded font-serif hover:bg-[#3e4a3c] transition"
            data-testid="signin-link"
          >
            Sign in →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e]" data-testid="wallet-page">
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="border-b-2 border-[#b58c2a] pb-4">
          <span className="text-4xl font-serif text-[#2c3e2a]">Your wallet</span>
        </div>
        <p className="mt-4 text-lg font-serif text-[#4a5a48] max-w-3xl">
          Every hour of paid work lands here immediately as in-store credit. Cash unlocks thirty days after each
          individual credit was earned. Monthly bank payout is default.
        </p>
      </section>

      {status.loading && (
        <section className="max-w-3xl mx-auto px-6 pb-8" data-testid="wallet-loading">
          <div className="bg-white border border-[#d9d3bd] rounded p-6 text-[#7c8a75] font-serif italic">
            Fetching your wallet…
          </div>
        </section>
      )}

      {status.error && (
        <section className="max-w-3xl mx-auto px-6 pb-8" data-testid="wallet-error">
          <div className="bg-white border border-[#d9d3bd] rounded p-6 text-[#4a5a48] font-serif">
            <div className="font-bold text-[#2c3e2a] mb-2">Something got in the way</div>
            <div className="text-sm">{status.error}</div>
          </div>
        </section>
      )}

      {wallet && (
        <>
          <section className="max-w-4xl mx-auto px-6 pb-8" data-testid="wallet-tiles">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Tile
                label="in-store credit"
                value={`$${Number(wallet.in_store_credit_usd || 0).toFixed(2)}`}
                subtitle="spend on any Baker and Sage product right now"
                testid="in-store"
              />
              <Tile
                label="cash unlocked"
                value={`$${Number(wallet.cash_unlocked_usd || 0).toFixed(2)}`}
                subtitle="past the 30-day window — payable to your bank"
                testid="cash-unlocked"
                accent="#2c3e2a"
                bright
              />
              <Tile
                label="cash pending"
                value={`$${Number(wallet.cash_pending_usd || 0).toFixed(2)}`}
                subtitle="inside the 30-day window still"
                testid="cash-pending"
              />
              <Tile
                label="lifetime earned"
                value={`$${Number(wallet.lifetime_earned_usd || 0).toFixed(2)}`}
                subtitle="every hour of work you have ever logged here"
                testid="lifetime-earned"
              />
            </div>
          </section>

          <section className="max-w-4xl mx-auto px-6 pb-8" data-testid="wallet-payout-plan">
            <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
              Payout plan
            </h2>
            <div className="bg-white border border-[#d9d3bd] rounded p-5">
              <div className="font-serif text-[#2c3e2a] font-bold text-lg">Monthly bank payout</div>
              <div className="font-serif text-sm text-[#4a5a48] mt-1">
                Default. Every cash-unlocked dollar lands in your bank on the first of the following month.
              </div>
              <div className="mt-4 font-serif text-[#7c8a75] italic text-sm">
                Weekly and daily payout are pledged. They turn on the day the till supports them.
              </div>
            </div>
          </section>

          {wallet.recent_credits && wallet.recent_credits.length > 0 && (
            <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="wallet-recent">
              <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
                Recent credits
              </h2>
              <div className="bg-white border border-[#d9d3bd] rounded overflow-hidden">
                <table className="w-full text-sm font-serif">
                  <thead className="bg-[#efeadc] text-[#2c3e2a]">
                    <tr>
                      <th className="text-left px-4 py-2">When</th>
                      <th className="text-left px-4 py-2">For</th>
                      <th className="text-right px-4 py-2">Amount</th>
                      <th className="text-right px-4 py-2">Cash unlocks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallet.recent_credits.map((c, i) => (
                      <tr key={i} className="border-t border-[#d9d3bd]">
                        <td className="px-4 py-2 text-[#4a5a48]">{c.at || "—"}</td>
                        <td className="px-4 py-2 text-[#4a5a48]">{c.reason || "work"}</td>
                        <td className="px-4 py-2 text-right text-[#2c3e2a] font-bold">${Number(c.amount_usd || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right text-[#7c8a75] italic">{c.unlocks_at || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-[#d9d3bd]">
        <p className="text-sm font-serif italic text-[#7c8a75]">
          Honest counters. Zero shows as zero. No promises the ledger cannot cash.
        </p>
      </footer>
    </div>
  );
}

function Tile({ label, value, subtitle, testid, accent = "#2c3e2a", bright = false }) {
  return (
    <div
      className={bright ? "bg-[#2c3e2a] border border-[#2c3e2a] rounded p-4" : "bg-white border border-[#d9d3bd] rounded p-4"}
      data-testid={testid}
    >
      <div className={bright ? "text-xs font-serif italic text-[#c8b78a] uppercase tracking-wide" : "text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide"}>
        {label}
      </div>
      <div className="text-2xl font-serif mt-1" style={{ color: bright ? "#fbfaf6" : accent }}>{value}</div>
      {subtitle && (
        <div className={bright ? "text-xs font-serif italic text-[#c8b78a] mt-2" : "text-xs font-serif italic text-[#7c8a75] mt-2"}>
          {subtitle}
        </div>
      )}
    </div>
  );
}
